import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { ichraUpdateBenefitStartDate } from 'core'
import { now } from 'core/application/commons/dateUtils'

import { Alert, Col, DatePicker, Row } from 'common/components'
import { addDays, parseZone, submitDateToServerFormat } from 'common/dateFormatters'
import { useCommandRequestV2 } from 'common/hooks'

import { BaseComponentProps } from '../../common/models'

import './styles.less'

type IchraBenefitStartDateProps = {
  benefitStartDate: any
  notificationDate: any
}

const IchraBenefitStartDate = (props: BaseComponentProps<IchraBenefitStartDateProps>) => {
  const { bindSubmitFormHandler } = props
  const { t: translate } = useTranslation()
  const { planPeriod } = useParams() as any

  const dayToCompare = props.state.notificationDate ? parseZone(props.state.notificationDate) : now()
  const [minBenefitStartDate, setMinBenefitStartDate] = useState(addDays(dayToCompare, 30))
  const [maxBenefitStartDate, setMaxBenefitStartDate] = useState(addDays(dayToCompare, 90))

  const { sendCommand: sendUpdateBenefitStartDate } = useCommandRequestV2((values: IchraBenefitStartDateProps) =>
    ichraUpdateBenefitStartDate({
      benefitStartDate: submitDateToServerFormat(values.benefitStartDate),
      notificationDate: submitDateToServerFormat(values.notificationDate),
      planPeriodId: planPeriod,
    }),
  )

  const [initialValues] = useState<IchraBenefitStartDateProps>({
    benefitStartDate: props.state.benefitStartDate,
    notificationDate: props.state.notificationDate,
  })

  useEffect(() => {
    props.setIsFormValid(Boolean(initialValues.notificationDate && initialValues.benefitStartDate))
  }, [initialValues])

  const onSubmit = async (values: IchraBenefitStartDateProps) => {
    const result = await sendUpdateBenefitStartDate(values)

    if (result.completed) {
      props.goNext()
    } else {
      props.setIsSubmiting(false)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
        props.setIsFormValid(formValidator(translate).isValidSync(values))
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form onSubmit={handleSubmit} autoComplete="off" className={'ichra-benefit-start-date'}>
            <h5 className={'mb-8'}>Schedule your official ICHRA Notification and Benefit Start Dates</h5>
            <p className={'mb-16'}>
              PeopleKeep will send a formal ICHRA Notice to your employees and invite them to participate in the
              benefit.{' '}
            </p>

            <Row>
              <Col sm={24} lg={6}>
                <h6 className={'mb-8'}>When should PeopleKeep notify your employees about their new health benefit?</h6>

                <DatePicker
                  className={'ichra-benefit-start-date__date-picker'}
                  name="notificationDate"
                  disabledDate={(d) => !d || d.isBefore(addDays(now(), -1)) || d.isAfter(addDays(now(), 90))}
                  helperText={
                    'The Benefit Start Date will calculate automatically based on your ICHRA Notification Date. '
                  }
                  value={formik.values.notificationDate ? parseZone(formik.values.notificationDate) : null}
                  onChange={(dateAsString) => {
                    if (!dateAsString) {
                      formik.setValues({
                        notificationDate: null,
                        benefitStartDate: null,
                      })
                      return
                    }

                    const date = parseZone(dateAsString)
                    const benefitStartDate = addDays(date, 30)
                    /*
                     * I set the value for the notification date because i need a date object. Need to find a better way to do this
                     * */
                    formik.setValues({
                      notificationDate: date,
                      benefitStartDate: benefitStartDate,
                    })
                    setMinBenefitStartDate(benefitStartDate)
                    setMaxBenefitStartDate(addDays(date, 90))
                  }}
                />

                {formik.values.notificationDate && (
                  <>
                    <h6 className={'mb-8 mt-24'}>
                      Based on your Notification Date, your earliest available ICHRA benefits start date is:
                    </h6>

                    <DatePicker
                      className={'ichra-benefit-start-date__date-picker'}
                      name="benefitStartDate"
                      disabledDate={(d) => !d || d.isBefore(minBenefitStartDate) || d.isAfter(maxBenefitStartDate)}
                      value={formik.values.benefitStartDate ? parseZone(formik.values.benefitStartDate) : null}
                    />

                    <Alert
                      type="info"
                      showIcon
                      description={
                        'Your Benefit Start Date must be no less than 30 and no more than 90 calendar days from your ICHRA Notification Date'
                      }
                    />
                  </>
                )}
              </Col>
            </Row>
          </form>
        )
      }}
    </Formik>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    notificationDate: Yup.date().required(translate('general.required')).nullable(),
    benefitStartDate: Yup.date().required(translate('general.required')).nullable(),
  })

export default IchraBenefitStartDate
